import { Form, Formik } from 'formik';
import React from 'react';
import {
  Button,
  CheckboxField,
  Grid,
  Header4Text,
  ISelectOption,
  NumberField,
  SelectField,
  TextField,
} from 'ui-toolkit/components';
import GrayLine from '../shared/GrayLine';
import { eLenderFeeType, prettyLenderFeeType } from 'types/lenders/ILenderInformation';
import { createLenderValidationFactory, ILenderForm } from 'validation/lenders/createLenderValidationFactory';
import { convertLenderFormToLenderRequest } from 'types/lenders/convertLenderFormToLenderRequest';
import { ILenderRequest } from '../../types/lenders/ILenderRequest';
import { StatesLabelValue } from '../../ui-toolkit/enums/states';

const feeTypeOptions: ISelectOption[] = Object.values(eLenderFeeType).map((feeType) => ({
  value: feeType,
  label: prettyLenderFeeType[feeType],
}));

const defaultInitialValues: ILenderForm = {
  name: '',
  address: {
    streetAddress: '',
    unit: undefined,
    city: '',
    zip: '',
    state: '',
  },
  mortgageeClause: '',
  masterEmail: '',
  wfgId: '',
  refiPremiumOrReportFee: eLenderFeeType.Premium,
  refiPageCounts: {
    lienRelease: 4,
    mortgage: 25,
    deed: undefined,
    noticeOfSettlement: undefined,
    subordinationAgreement: undefined,
    assignment: undefined,
  },
  purchasePageCounts: {
    lienRelease: 4,
    mortgage: 25,
    deed: 4,
    noticeOfSettlement: undefined,
    subordinationAgreement: undefined,
    assignment: undefined,
  },
  bulkRateQualified: false,
  refiDiscountedClosingFee: false,
  coBranded: false,
  logoUrl: undefined,
};

export interface ILenderCompanyFormProps {
  onSubmit: (x: ILenderRequest) => Promise<void>;
  initialValues?: ILenderForm;
  onSubmitLabel: string;
}

export const LenderCompanyForm: React.FC<ILenderCompanyFormProps> = ({ onSubmit, initialValues, onSubmitLabel }) => {
  const stateFilterOptions = (option: ISelectOption<string>, value: string) => {
    return option.value.toLowerCase().indexOf((value || '').toLowerCase()) >= 0;
  };
  return (
    <Formik
      initialValues={initialValues || defaultInitialValues}
      onSubmit={(values) => {
        onSubmit(convertLenderFormToLenderRequest(values));
      }}
      validationSchema={createLenderValidationFactory()}
    >
      {({ values }) => (
        <Form>
          <Grid>
            <TextField name={'name'} label={'Name'} isRequired />
            <TextField name={'masterEmail'} label={'Master Email'} />
            <TextField name={'mortgageeClause'} label={'Mortgagee Clause'} />
            <SelectField name={'refiPremiumOrReportFee'} options={feeTypeOptions} label={'Refinance Product Type'} />
            <CheckboxField name={'bulkRateQualified'} text={'Qualifies for Bulk Refinance Premiums'} />
            <CheckboxField name={'refiDiscountedClosingFee'} text={'Discounted Closing Fee (Refinance Transactions)'} />
            <CheckboxField name={'coBranded'} text={'Co-Branded'} />
            {values.coBranded && (
              <TextField
                name={'logoUrl'}
                label={'Logo Url'}
                placeholder={'https://www.example.com/logo.png'}
                isRequired
              />
            )}
            <GrayLine />
            <Header4Text>Refinance - Number of pages for recording fees</Header4Text>
            <Grid columnCount={6}>
              <NumberField name={'refiPageCounts.lienRelease'} label={'Lien release'} />
              <NumberField name={'refiPageCounts.mortgage'} label={'Mortgage'} />
              <NumberField name={'refiPageCounts.deed'} label={'Deed'} />
              <NumberField name={'refiPageCounts.noticeOfSettlement'} label={'Notice of settlement'} />
              <NumberField name={'refiPageCounts.subordinationAgreement'} label={'Subordination agreement'} />
              <NumberField name={'refiPageCounts.assignment'} label={'Assignment'} />
            </Grid>
            <GrayLine /> <Header4Text>Purchase - Number of pages for recording fees</Header4Text>
            <Grid columnCount={6}>
              <NumberField name={'purchasePageCounts.lienRelease'} label={'Lien release'} />
              <NumberField name={'purchasePageCounts.mortgage'} label={'Mortgage'} />
              <NumberField name={'purchasePageCounts.deed'} label={'Deed'} />
              <NumberField name={'purchasePageCounts.noticeOfSettlement'} label={'Notice of settlement'} />
              <NumberField name={'purchasePageCounts.subordinationAgreement'} label={'Subordination agreement'} />
              <NumberField name={'purchasePageCounts.assignment'} label={'Assignment'} />
            </Grid>
            <GrayLine />
            <Header4Text>Address Info</Header4Text>
            <Grid templateColumns={'3fr 1fr'}>
              <TextField name={'address.streetAddress'} label={'Street Address'} isRequired />
              <TextField name={'address.unit'} label={'Unit'} />
            </Grid>
            <Grid templateColumns={'5fr 2fr 5fr'}>
              <TextField name={'address.city'} label={'City'} isRequired />
              <SelectField
                name={'address.state'}
                label={'State'}
                filterOption={stateFilterOptions}
                options={StatesLabelValue}
                isRequired
              />
              <TextField name={'address.zip'} label={'ZIP'} isRequired />
            </Grid>
            <Grid templateColumns={'1fr 4fr'}>
              <Button type={'submit'} intent={'primary'}>
                {onSubmitLabel}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
